import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

/**
 * ─────────────────────────────────────────────────────────────────────────────
 *  CONTACTS EXTERNES DES CAMPAGNES (adresses e-mail sans compte)
 * ─────────────────────────────────────────────────────────────────────────────
 *  Bibliothèque d'adresses importées depuis CSV — destinataires d'e-mails
 *  directs (aucun compte User créé). Désabonnement RGPD via blacklist.
 * ─────────────────────────────────────────────────────────────────────────────
 */

export const CONTACT_SOURCES = ["csv", "manual"] as const;

export const contactFilterSchema = z.object({
  search: z.string().max(200).optional(),
  /** all | only | exclude (défaut : exclure les blacklistés) */
  blacklisted: z.enum(["all", "only", "exclude"]).default("exclude"),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  cursor: z.string().optional(),
});
export class ContactFilterDto extends createZodDto(contactFilterSchema) {}

export const blacklistContactSchema = z.object({
  email: z.string().email(),
  blacklisted: z.boolean().default(true),
});
export class BlacklistContactDto extends createZodDto(blacklistContactSchema) {}

/** Désabonnement public (lien présent dans chaque e-mail externe). */
export const unsubscribeSchema = z.object({
  contactId: z.string().min(1),
  token: z.string().min(10).max(200),
});
export class UnsubscribeDto extends createZodDto(unsubscribeSchema) {}

/** Aperçu e-mail rendu côté serveur (HTML final dans l'iframe admin). */
export const previewEmailSchema = z.object({
  title: z.string().min(1).max(200),
  body: z.string().max(5000).optional(),
  link: z.string().max(500).optional(),
  emailSubject: z.string().max(200).optional(),
  emailPreheader: z.string().max(300).optional(),
  emailBlocks: z.record(z.unknown()).optional(),
  /** Personnalisation de démonstration. */
  sampleName: z.string().max(100).optional(),
  sampleEmail: z.string().max(200).optional(),
});
export class PreviewEmailDto extends createZodDto(previewEmailSchema) {}

/** Contact exposé (lecture). */
export const campaignContactSchema = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable().optional(),
  source: z.string(),
  isBlacklisted: z.boolean(),
  blacklistedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});
export type CampaignContactDto = z.infer<typeof campaignContactSchema>;

export const paginatedContactsSchema = z.object({
  items: z.array(campaignContactSchema),
  nextCursor: z.string().nullable(),
  total: z.number().int().min(0).optional(),
});
export class PaginatedContactsDto extends createZodDto(paginatedContactsSchema) {}

/** Rapport d'import CSV (contacts externes). */
export const contactImportResultSchema = z.object({
  totalRows: z.number().int().min(0),
  imported: z.number().int().min(0),
  duplicatesInFile: z.number().int().min(0),
  alreadyExisting: z.number().int().min(0),
  invalid: z.number().int().min(0),
  /** Détail des lignes rejetées (20 max). */
  errors: z.array(
    z.object({
      line: z.number().int(),
      email: z.string().nullable(),
      reason: z.string(),
    }),
  ),
});
export class ContactImportResultDto extends createZodDto(contactImportResultSchema) {}

/** Réponse de désabonnement public. */
export const unsubscribeResultSchema = z.object({ ok: z.boolean() });
export class UnsubscribeResultDto extends createZodDto(unsubscribeResultSchema) {}
